import { useEffect, useState } from "react";
import { Brand } from "@/types/supabase";
import { fetchWithAuth } from "@/lib/api";
import { useAuth } from "./useAuth";

export function useBrand() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken } = useAuth();
  useEffect(() => {
    // Fetch brands from Supabase
    const fetchBrands = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = await getAccessToken();
        const { data: brandsData, error: brandsError } = await fetchWithAuth(
          "/branch/brands?limit=100",
          token,
        );
        if (brandsError) throw brandsError;

        if (!brandsData) {
          setBrands([]);
          return;
        }

        const transformedBrands: Brand[] = brandsData.map((brand: Brand) => {
          return {
            id: Number(brand.id),
            name: String(brand.name),
            branches:
              brand.branches?.map((branch) => {
                // Handle the case where service_zone is an object with coordinates array
                const serviceZone = branch.service_zone as unknown as {
                  id: number;
                  coordinates: Array<{ lat: number; lon: number }>;
                } | null;

                let coordinates: { lat: number; lon: number }[] = [];
                if (
                  serviceZone?.coordinates &&
                  Array.isArray(serviceZone.coordinates)
                ) {
                  coordinates = serviceZone.coordinates.map((coord) => ({
                    lat: coord.lat,
                    lon: coord.lon,
                  }));
                }

                return {
                  id: Number(branch.id),
                  name: String(branch.name),
                  location_lat: Number(branch.location_lat),
                  location_lon: Number(branch.location_lon),
                  created_at: String(branch.created_at),
                  full_address: String(branch.full_address ?? ""),
                  is_active: branch.is_active ?? true,
                  service_zone: {
                    id: Number(serviceZone?.id || 0),
                    coordinates: {
                      type: "Polygon",
                      coordinates: coordinates.length > 0 ? coordinates : [],
                    },
                  },
                  drivers:
                    branch.drivers?.map((driver) => ({
                      id: Number(driver.id),
                      name: String(driver.name),
                      location_lat: Number(driver.location_lat),
                      location_lon: Number(driver.location_lon),
                      status: driver.available ? "available" : "busy",
                    })) || [],
                };
              }) || [],
          };
        });

        setBrands(transformedBrands);
      } catch (err) {
        console.error("Error fetching brands:", err);
        setError(
          err instanceof Error ? err.message : "Error al cargar las marcas",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, []);

  return { brands, loading, error };
}
